import { chunk, map, set } from 'lodash'
import pluralize from 'pluralize'
import { IProduct, IProductPagedData } from '../interfaces/product'

import cache from '../utils/cache'
import { http } from '../utils/http'

class BaseService {
  protected model: string
  protected http: any
  protected cache: any

  constructor(model: string) {
    this.model = model

    // Utils
    this.http = http
    this.cache = cache
  }

  fetch(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const cacheKey = `${pluralize(this.model)}`
      const cachedData = cache.get(cacheKey)

      if (cachedData) resolve(cachedData)
      else {
        try {
          http.get(`${this.model}/read_all`).then((data) => {
            if (data.code === 200) {
              cache.set(cacheKey, data.data)
              resolve(data.data)
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchById(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const cacheKey = `${this.model}_${id}`
      const cachedData = cache.get(cacheKey)
      if (cachedData) resolve(cachedData)
      else {
        try {
          http.get(`${this.model}/read/?_id=${id}`).then((data) => {
            if (data.code === 200 && data.data.length) {
              cache.set(cacheKey, data.data[0])
              resolve(data.data[0])
            } else reject({ message: data.message })
          })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  fetchByStatus(status: string): Promise<any[]> {
    const formData = new FormData()
    formData.append('status', status)

    return new Promise((resolve, reject) => {
      const cacheKey = `${pluralize(this.model)}_${status}`
      const cachedData = cache.get(cacheKey)

      if (cachedData) resolve(cachedData)
      else {
        try {
          http
            .post(`${this.model}/read_by_status`, formData)
            .then((data) => {
              if (data.code === 200) {
                cache.set(cacheKey, data.data)
                resolve(data.data)
              } else reject({ message: data.message })
            })
        } catch (error) {
          reject({ message: 'An unexpected error occured!' })
          throw error
        }
      }
    })
  }

  signup(model: any): Promise<any> {
    const formData = new FormData()

    Object.keys(model).forEach((key) => {
      formData.append(key, model[key])
    })

    return new Promise((resolve, reject) => {
      try {
        http.post(`${this.model}/signup`, formData).then((data) => {
          if (data.code === 200) {
            cache.reset()
            resolve(data.data)
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  create(
    payload: any,
    options?: { bulk?: boolean; resetCache?: boolean }
  ): Promise<any> {
    options = { bulk: false, resetCache: true, ...options }
    const formData = new FormData()

    if (options.bulk && payload?.length) {
      payload.forEach((item) => {
        Object.keys(item).forEach((key) => {
          formData.append(`${key}[]`, item[key])
        })
      })
    } else {
      Object.keys(payload).forEach((key) => {
        formData.append(key, payload[key])
      })
    }

    const endpoint = options.bulk ? 'create_bulk' : 'create'

    return new Promise((resolve, reject) => {
      try {
        http.post(`${this.model}/${endpoint}`, formData).then((data) => {
          if (data.code === 200) {
            options.resetCache && cache.reset()
            resolve(data.data)
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  update(model: any): Promise<any> {
    const formData = new FormData()

    Object.keys(model).forEach((key) => {
      formData.append(key, model[key])
    })

    return new Promise((resolve, reject) => {
      try {
        http.post(`${this.model}/update`, formData).then((data) => {
          if (data.code === 200) {
            cache.reset()
            resolve(data.data)
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  delete(modelId: string): Promise<any> {
    const formData = new FormData()
    formData.append('_id', modelId)

    return new Promise((resolve, reject) => {
      try {
        http.post(`${this.model}/delete`, formData).then((data) => {
          if (data.code === 200) {
            cache.reset()
            resolve(data.data)
          } else reject({ message: data.message })
        })
      } catch (error) {
        reject({ message: 'An unexpected error occured!' })
        throw error
      }
    })
  }

  chunker(data: IProduct[], chunkSize: number, base: IProductPagedData[], index: number, otherData: any): IProductPagedData[] {
    let data_copy = Array.from(data).slice(0, chunkSize)
    if (data.length > chunkSize) {
      base.push({
        ...otherData,
        page: index,
        limit: chunkSize,
        data: data_copy,
      })
      let newData = Array.from(data).slice(chunkSize)
      return this.chunker(newData, chunkSize, Array.from(base), index + 1, otherData)
    } else {
      base.push({
        ...otherData,
        page: index,
        limit: chunkSize,
        data: data_copy,
      })
      return base
    }
  }

  lodashChunker(data: IProduct[], chunkSize: number): Object {
    let chunks: IProduct[][] = chunk(data, chunkSize)
    let chunksFormatted = {}

    chunks.forEach((products, index) => {
      set(chunksFormatted, index, {
        page: index + 1,
        total: chunks.length,
        count: data.length,
        limit: chunkSize,
        data: products
      })

    });
    return chunksFormatted
  }

  saveChunks(_key: string, products: IProduct[], limit: number): IProductPagedData[] {
    const data_copy = Array.from(products)
    const chunkedData: any = Object.assign({}, this.lodashChunker(data_copy, limit))
    map(chunkedData, (pagedData: IProductPagedData) => {
      const cacheKey = `${_key}_${pagedData.page}_${pagedData.limit}`;
      this.cache.set(cacheKey, pagedData)
    });
    return chunkedData
  }
}

export type BaseServiceProps = keyof BaseService
export default BaseService
